import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../lib/api';
import { CreditCard, Eye, EyeOff, Save, RefreshCw, CheckCircle, XCircle } from 'lucide-react';
import { toast } from 'sonner';

const AdminPaymentGatewayPage = () => {
  const [stripe, setStripe] = useState({ enabled: false, public_key: '', secret_key: '' });
  const [razorpay, setRazorpay] = useState({ enabled: false, key_id: '', key_secret: '' });
  const [showStripeSecret, setShowStripeSecret] = useState(false);
  const [showRazorpaySecret, setShowRazorpaySecret] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchSettings(); }, []);

  const fetchSettings = async () => {
    try {
      const res = await adminAPI.getPaymentGateway();
      const pg = res.data.payment_gateway || {};
      setStripe({
        enabled: pg.stripe_enabled || false,
        public_key: pg.stripe_public_key || '',
        secret_key: pg.stripe_secret_key || ''
      });
      setRazorpay({
        enabled: pg.razorpay_enabled || false,
        key_id: pg.razorpay_key_id || '',
        key_secret: pg.razorpay_key_secret || ''
      });
    } catch { toast.error('Failed to load settings'); }
    finally { setLoading(false); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await adminAPI.updatePaymentGateway({
        stripe_enabled: stripe.enabled,
        stripe_public_key: stripe.public_key,
        stripe_secret_key: stripe.secret_key === '••••••••' ? undefined : stripe.secret_key,
        razorpay_enabled: razorpay.enabled,
        razorpay_key_id: razorpay.key_id,
        razorpay_key_secret: razorpay.key_secret === '••••••••' ? undefined : razorpay.key_secret
      });
      toast.success('Payment gateway settings saved!');
    } catch { toast.error('Failed to save settings'); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin w-8 h-8 border-2 border-[#1A73E8] border-t-transparent rounded-full" /></div>;

  return (
    <div className="space-y-6 animate-fade-in-up">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-[#202124]" style={{ fontFamily: 'Outfit' }}>Payment Gateway</h1>
          <p className="text-[#5F6368]">Configure Stripe (USD) and Razorpay (INR) credentials</p>
        </div>
        <button onClick={handleSave} disabled={saving} className="btn-google flex items-center gap-2" data-testid="save-pg-btn">
          {saving ? <RefreshCw className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      {/* Stripe (USD) */}
      <div className="dashboard-card">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-[#635BFF]/10 rounded-xl flex items-center justify-center">
              <CreditCard className="w-6 h-6 text-[#635BFF]" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-[#202124]" style={{ fontFamily: 'Outfit' }}>Stripe</h3>
              <p className="text-sm text-[#5F6368]">For USD payments</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {stripe.enabled ? <CheckCircle className="w-5 h-5 text-[#1E8E3E]" /> : <XCircle className="w-5 h-5 text-[#DADCE0]" />}
            <button
              onClick={() => setStripe({ ...stripe, enabled: !stripe.enabled })}
              className={`w-12 h-6 rounded-full transition-colors ${stripe.enabled ? 'bg-[#1E8E3E]' : 'bg-[#DADCE0]'}`}
              data-testid="stripe-toggle"
            >
              <div className={`w-5 h-5 rounded-full bg-white shadow transition-transform ${stripe.enabled ? 'translate-x-6' : 'translate-x-0.5'}`} />
            </button>
          </div>
        </div>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-[#5F6368] mb-1">Publishable Key</label>
            <input type="text" value={stripe.public_key} onChange={(e) => setStripe({ ...stripe, public_key: e.target.value })}
              className="input-google" placeholder="pk_live_..." data-testid="stripe-public-key" />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#5F6368] mb-1">Secret Key</label>
            <div className="relative">
              <input type={showStripeSecret ? "text" : "password"} value={stripe.secret_key}
                onChange={(e) => setStripe({ ...stripe, secret_key: e.target.value })}
                className="input-google pr-10" placeholder="sk_live_..." data-testid="stripe-secret-key" />
              <button onClick={() => setShowStripeSecret(!showStripeSecret)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#5F6368]">
                {showStripeSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Razorpay (INR) */}
      <div className="dashboard-card">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-[#072654]/10 rounded-xl flex items-center justify-center">
              <CreditCard className="w-6 h-6 text-[#072654]" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-[#202124]" style={{ fontFamily: 'Outfit' }}>Razorpay</h3>
              <p className="text-sm text-[#5F6368]">For INR payments</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {razorpay.enabled ? <CheckCircle className="w-5 h-5 text-[#1E8E3E]" /> : <XCircle className="w-5 h-5 text-[#DADCE0]" />}
            <button
              onClick={() => setRazorpay({ ...razorpay, enabled: !razorpay.enabled })}
              className={`w-12 h-6 rounded-full transition-colors ${razorpay.enabled ? 'bg-[#1E8E3E]' : 'bg-[#DADCE0]'}`}
              data-testid="razorpay-toggle"
            >
              <div className={`w-5 h-5 rounded-full bg-white shadow transition-transform ${razorpay.enabled ? 'translate-x-6' : 'translate-x-0.5'}`} />
            </button>
          </div>
        </div>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-[#5F6368] mb-1">Key ID</label>
            <input type="text" value={razorpay.key_id} onChange={(e) => setRazorpay({ ...razorpay, key_id: e.target.value })}
              className="input-google" placeholder="rzp_live_..." data-testid="razorpay-key-id" />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#5F6368] mb-1">Key Secret</label>
            <div className="relative">
              <input type={showRazorpaySecret ? "text" : "password"} value={razorpay.key_secret}
                onChange={(e) => setRazorpay({ ...razorpay, key_secret: e.target.value })}
                className="input-google pr-10" placeholder="Enter key secret..." data-testid="razorpay-key-secret" />
              <button onClick={() => setShowRazorpaySecret(!showRazorpaySecret)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#5F6368]">
                {showRazorpaySecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPaymentGatewayPage;
